import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Users, Plus, Search, TrendingUp, DollarSign, Copy, CheckCircle2,
  XCircle, Edit2, Trash2, Download, Calendar, Building2, UserPlus,
  Link2, ExternalLink, Mail, Phone, Linkedin, BarChart3, Clock,
  RefreshCw, FileText, Award, Play, Settings, Lock, Eye, EyeOff
} from 'lucide-react';

export default function ReferralManagement() {
  const [activeTab, setActiveTab] = useState('overview');
  const [referrers, setReferrers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [runningJob, setRunningJob] = useState(null);

  // Referrer form state
  const [referrerForm, setReferrerForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    commission_rate: 10,
    notes: ''
  });

  // Commission calculation form
  const [commissionForm, setCommissionForm] = useState({
    period_start: new Date().toISOString().slice(0, 7) + '-01',
    period_end: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
    per_employee_rate: 25
  });

  const fetchReferrers = useCallback(async () => {
    try {
      const response = await api.get('/referrals/referrers?active_only=false');
      setReferrers(response.data.referrers || []);
    } catch (error) {
      console.error('Failed to fetch referrers:', error);
      setReferrers([]);
    }
  }, []);

  const fetchCommissions = useCallback(async () => {
    try {
      const response = await api.get('/referrals/commissions');
      setCommissions(response.data.commissions || []);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
      setCommissions([]);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/referrals/reports/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchReferrers(), fetchSummary()]);
      setLoading(false);
    };
    loadData();
  }, [fetchReferrers, fetchSummary]);

  useEffect(() => {
    if (activeTab === 'commissions') {
      fetchCommissions();
    }
    if (activeTab === 'automation') {
      fetchScheduledJobs();
    }
  }, [activeTab, fetchCommissions]);

  const fetchScheduledJobs = async () => {
    try {
      const response = await api.get('/referrals/scheduler/jobs');
      setScheduledJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch scheduled jobs:', error);
      setScheduledJobs([]);
    }
  };

  const handleRunJob = async (jobId) => {
    try {
      setRunningJob(jobId);
      const response = await api.post(`/referrals/scheduler/run/${jobId}`);
      toast.success(response.data.message || 'Job executed successfully');
      fetchScheduledJobs();
    } catch (error) {
      console.error('Failed to run job:', error);
      toast.error(error.response?.data?.detail || 'Failed to run job');
    } finally {
      setRunningJob(null);
    }
  };

  const handleCreateReferrer = async () => {
    try {
      if (!referrerForm.full_name || !referrerForm.email) {
        toast.error('Name and email are required');
        return;
      }
      
      await api.post('/referrals/referrers', referrerForm);
      toast.success('Referrer created successfully');
      setShowReferrerModal(false);
      resetReferrerForm();
      fetchReferrers();
      fetchSummary();
    } catch (error) {
      console.error('Failed to create referrer:', error);
      toast.error(error.response?.data?.detail || 'Failed to create referrer');
    }
  };

  const handleUpdateReferrer = async () => {
    try {
      await api.put(`/referrals/referrers/${editingReferrer.id}`, referrerForm);
      toast.success('Referrer updated successfully');
      setShowReferrerModal(false);
      setEditingReferrer(null);
      resetReferrerForm();
      fetchReferrers();
    } catch (error) {
      console.error('Failed to update referrer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update referrer');
    }
  };

  const handleCalculateCommissions = async () => {
    try {
      const params = new URLSearchParams({
        period_start: commissionForm.period_start,
        period_end: commissionForm.period_end,
        per_employee_rate: commissionForm.per_employee_rate
      });
      
      const response = await api.post(`/referrals/commissions/calculate?${params}`);
      
      if (response.data.calculated > 0) {
        toast.success(`Calculated ${response.data.calculated} commission(s)`);
      } else {
        toast.info('No commissions to calculate');
      }
      
      setShowCommissionModal(false);
      fetchCommissions();
      fetchSummary();
    } catch (error) {
      console.error('Failed to calculate commissions:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate commissions');
    }
  };

  const handleUpdateCommissionStatus = async (commissionId, newStatus, paymentRef = null) => {
    try {
      await api.put(`/referrals/commissions/${commissionId}/status`, {
        status: newStatus,
        payment_reference: paymentRef
      });
      toast.success(`Commission marked as ${newStatus}`);
      fetchCommissions();
      fetchSummary();
    } catch (error) {
      console.error('Failed to update commission:', error);
      toast.error('Failed to update commission status');
    }
  };

  const [sendingReports, setSendingReports] = useState(false);

  const handleSendMonthlyReports = async () => {
    try {
      setSendingReports(true);
      const response = await api.post('/referrals/commissions/send-monthly-reports');
      
      if (response.data.reports_sent > 0) {
        toast.success(`Sent ${response.data.reports_sent} commission report(s)`);
      } else {
        toast.info(response.data.message || 'No reports to send');
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some reports failed:', response.data.errors);
        toast.warning(`${response.data.errors.length} report(s) failed to send`);
      }
    } catch (error) {
      console.error('Failed to send reports:', error);
      toast.error(error.response?.data?.detail || 'Failed to send monthly reports');
    } finally {
      setSendingReports(false);
    }
  };

  const handleExportCommissions = async () => {
    try {
      const response = await api.get('/referrals/reports/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'referral_commissions.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Commissions exported successfully');
    } catch (error) {
      console.error('Failed to export commissions:', error);
      toast.error('Failed to export commissions');
    }
  };

  const copyReferralLink = (code) => {
    const link = `https://integrityhomecarenc.com?ref=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    toast.success('Referral link copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const resetReferrerForm = () => {
    setReferrerForm({
      full_name: '',
      email: '',
      phone: '',
      linkedin_url: '',
      commission_rate: 10,
      notes: ''
    });
  };

  const openEditReferrer = (referrer) => {
    setEditingReferrer(referrer);
    setReferrerForm({
      full_name: referrer.full_name || '',
      email: referrer.email || '',
      phone: referrer.phone || '',
      linkedin_url: referrer.linkedin_url || '',
      commission_rate: referrer.commission_rate || 10,
      notes: referrer.notes || ''
    });
    setShowReferrerModal(true);
  };

  const [showPortalPasswordModal, setShowPortalPasswordModal] = useState(false);
  const [portalPasswordReferrer, setPortalPasswordReferrer] = useState(null);
  const [portalPassword, setPortalPassword] = useState('');
  const [portalPasswordConfirm, setPortalPasswordConfirm] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);

  const handleSetPortalPassword = async () => {
    if (portalPassword !== portalPasswordConfirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (portalPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      setSettingPassword(true);
      await api.post(`/referrals/portal/set-password/${portalPasswordReferrer.id}`, {
        password: portalPassword,
        confirm_password: portalPasswordConfirm
      });
      toast.success('Portal access enabled! Referrer can now log in at /referrer-portal');
      setShowPortalPasswordModal(false);
      setPortalPassword('');
      setPortalPasswordConfirm('');
      setPortalPasswordReferrer(null);
      fetchReferrers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSettingPassword(false);
    }
  };

  const openPortalPasswordModal = (referrer) => {
    setPortalPasswordReferrer(referrer);
    setPortalPassword('');
    setPortalPasswordConfirm('');
    setShowPortalPasswordModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const filteredReferrers = referrers.filter(r =>
    r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      approved: 'bg-blue-100 text-blue-700 border-blue-200',
      paid: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  return (
    <div className="space-y-6" data-testid="referral-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Referral Program
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage referrers, track commissions, and grow your partner network
          </p>
        </div>
        <Button
          onClick={() => {
            resetReferrerForm();
            setEditingReferrer(null);
            setShowReferrerModal(true);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
          data-testid="add-referrer-btn"
        >
          <UserPlus className="w-4 h-4" />
          Add Referrer
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-purple-50/50 p-1 rounded-xl border border-purple-100/60">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="referrers" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <Users className="w-4 h-4" />
            Referrers ({referrers.length})
          </TabsTrigger>
          <TabsTrigger 
            value="commissions" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <DollarSign className="w-4 h-4" />
            Commissions
          </TabsTrigger>
          <TabsTrigger 
            value="automation" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <Settings className="w-4 h-4" />
            Automation
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="card-stat group">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-purple-100 to-purple-200 border-purple-200">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{summary?.total_referrers || 0}</p>
                  <p className="text-sm text-gray-500">Active Referrers</p>
                </div>
              </div>
            </div>

            <div className="card-stat group">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-blue-100 to-blue-200 border-blue-200">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{summary?.total_referred_companies || 0}</p>
                  <p className="text-sm text-gray-500">Referred Companies</p>
                </div>
              </div>
            </div>

            <div className="card-stat group">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-green-100 to-green-200 border-green-200">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(summary?.total_paid)}</p>
                  <p className="text-sm text-gray-500">Total Paid Out</p>
                </div>
              </div>
            </div>

            <div className="card-stat group">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-amber-100 to-amber-200 border-amber-200">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(summary?.total_pending)}</p>
                  <p className="text-sm text-gray-500">Pending Payouts</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Referrers & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Referrers */}
            <Card className="card-premium p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Top Referrers
                </h3>
                <Award className="w-5 h-5 text-amber-500" />
              </div>
              
              {summary?.top_referrers?.length > 0 ? (
                <div className="space-y-3">
                  {summary.top_referrers.map((referrer, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50/50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-amber-100 text-amber-700' :
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-amber-600/20 text-amber-800' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{referrer.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-purple-900">{referrer.count}</p>
                        <p className="text-xs text-gray-500">referrals</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No referrals yet</p>
                </div>
              )}
            </Card>

            {/* Quick Actions */}
            <Card className="card-premium p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Quick Actions
                </h3>
              </div>
              
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => {
                    resetReferrerForm();
                    setShowReferrerModal(true);
                  }}
                >
                  <UserPlus className="w-5 h-5 text-purple-600" />
                  <span>Add New Referrer</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={() => setShowCommissionModal(true)}
                >
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <span>Calculate Commissions</span>
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-12"
                  onClick={handleExportCommissions}
                >
                  <Download className="w-5 h-5 text-blue-600" />
                  <span>Export Commission Report</span>
                </Button>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Referrers Tab */}
        <TabsContent value="referrers" className="space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search referrers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Referrers List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-3 text-gray-500">Loading referrers...</p>
            </div>
          ) : filteredReferrers.length === 0 ? (
            <Card className="card-premium p-12 text-center">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Referrers Yet</h3>
              <p className="text-gray-500 mb-6">
                Start building your referral network by adding your first referrer.
              </p>
              <Button
                onClick={() => {
                  resetReferrerForm();
                  setShowReferrerModal(true);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Add Your First Referrer
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredReferrers.map((referrer) => (
                <Card key={referrer.id} className="card-premium p-5" data-testid={`referrer-card-${referrer.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-bold text-lg">
                        {referrer.full_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{referrer.full_name}</h4>
                        <p className="text-sm text-gray-500">{referrer.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPortalPasswordModal(referrer)}
                        className="text-gray-500 hover:text-green-700"
                        title="Enable Portal Access"
                      >
                        <Lock className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditReferrer(referrer)}
                        className="text-gray-500 hover:text-purple-700"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 bg-purple-50 rounded-lg">
                      <p className="text-lg font-bold text-purple-900">{referrer.total_referrals || 0}</p>
                      <p className="text-xs text-gray-500">Referrals</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <p className="text-lg font-bold text-green-700">{formatCurrency(referrer.total_earned)}</p>
                      <p className="text-xs text-gray-500">Earned</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <p className="text-lg font-bold text-amber-700">{referrer.commission_rate}%</p>
                      <p className="text-xs text-gray-500">Rate</p>
                    </div>
                  </div>

                  {/* Contact & Code */}
                  <div className="space-y-2 text-sm">
                    {referrer.phone && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-3.5 h-3.5" />
                        {referrer.phone}
                      </div>
                    )}
                    {referrer.linkedin_url && (
                      <a 
                        href={referrer.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                        LinkedIn Profile
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>

                  {/* Referral Code */}
                  {referrer.codes && referrer.codes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Referral Code</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-gray-100 rounded-lg font-mono text-sm">
                          {referrer.codes[0].code}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyReferralLink(referrer.codes[0].code)}
                          className="gap-1"
                        >
                          {copiedCode === referrer.codes[0].code ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Commission History</h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCommissionModal(true)}
                className="gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Calculate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCommissions}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
              <Button
                size="sm"
                onClick={handleSendMonthlyReports}
                disabled={sendingReports}
                className="gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                data-testid="send-monthly-reports-btn"
              >
                {sendingReports ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                Send Reports
              </Button>
            </div>
          </div>

          {commissions.length === 0 ? (
            <Card className="card-premium p-8 text-center">
              <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No commissions calculated yet</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setShowCommissionModal(true)}
              >
                Calculate Commissions
              </Button>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-purple-50 border-b border-purple-100">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Referrer</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Company</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-purple-900">Employees</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-purple-900">Commission</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-purple-900">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-purple-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((commission) => (
                    <tr key={commission.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-sm">
                        {new Date(commission.period_start).toLocaleDateString()} - {new Date(commission.period_end).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {commission.referrers?.full_name || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {commission.tenants?.name || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {commission.employee_count}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-green-700">
                        {formatCurrency(commission.commission_amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(commission.status)}`}>
                          {commission.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {commission.status === 'pending' && (
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpdateCommissionStatus(commission.id, 'approved')}
                              title="Approve"
                            >
                              <CheckCircle2 className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const ref = prompt('Enter payment reference (optional):');
                                handleUpdateCommissionStatus(commission.id, 'paid', ref);
                              }}
                              title="Mark Paid"
                            >
                              <DollarSign className="w-4 h-4 text-green-600" />
                            </Button>
                          </div>
                        )}
                        {commission.status === 'approved' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const ref = prompt('Enter payment reference (optional):');
                              handleUpdateCommissionStatus(commission.id, 'paid', ref);
                            }}
                            title="Mark Paid"
                          >
                            <DollarSign className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Scheduled Jobs</h3>
              <p className="text-sm text-gray-500 mt-1">Automated commission calculations and email reports</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchScheduledJobs}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>

          <div className="grid gap-4">
            {/* Auto Calculate Commissions Job */}
            <Card className="card-premium p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-200 rounded-xl">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Auto Calculate Monthly Commissions</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Automatically calculates commissions for all referred companies based on employee counts.
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>1st of each month @ 8:00 AM</span>
                      </div>
                      {scheduledJobs.find(j => j.id === 'auto_calculate_commissions')?.next_run && (
                        <div className="flex items-center gap-2 text-sm text-purple-600">
                          <Clock className="w-4 h-4" />
                          <span>Next: {new Date(scheduledJobs.find(j => j.id === 'auto_calculate_commissions').next_run).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleRunJob('auto_calculate_commissions')}
                  disabled={runningJob === 'auto_calculate_commissions'}
                  className="gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  data-testid="run-calculate-job-btn"
                >
                  {runningJob === 'auto_calculate_commissions' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Run Now
                </Button>
              </div>
            </Card>

            {/* Send Monthly Reports Job */}
            <Card className="card-premium p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-purple-100 to-violet-200 rounded-xl">
                    <Mail className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Send Monthly Commission Reports</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Emails commission reports to all referrers with their earnings breakdown.
                    </p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>1st of each month @ 9:00 AM</span>
                      </div>
                      {scheduledJobs.find(j => j.id === 'monthly_commission_reports')?.next_run && (
                        <div className="flex items-center gap-2 text-sm text-purple-600">
                          <Clock className="w-4 h-4" />
                          <span>Next: {new Date(scheduledJobs.find(j => j.id === 'monthly_commission_reports').next_run).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleRunJob('monthly_commission_reports')}
                  disabled={runningJob === 'monthly_commission_reports'}
                  className="gap-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
                  data-testid="run-reports-job-btn"
                >
                  {runningJob === 'monthly_commission_reports' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  Run Now
                </Button>
              </div>
            </Card>
          </div>

          {/* Info Card */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/50 p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900">How Automation Works</h4>
                <p className="text-sm text-blue-700 mt-1">
                  On the 1st of each month, the system automatically:
                </p>
                <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Calculates commissions for all referred companies (8:00 AM)</li>
                  <li>Sends personalized email reports to each referrer (9:00 AM)</li>
                  <li>Notifies referrers in real-time when their referred companies add new employees</li>
                </ol>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Referrer Modal */}
      {showReferrerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {editingReferrer ? 'Edit Referrer' : 'Add New Referrer'}
                </h3>
                <button
                  onClick={() => {
                    setShowReferrerModal(false);
                    setEditingReferrer(null);
                    resetReferrerForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={referrerForm.full_name}
                  onChange={(e) => setReferrerForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="e.g., Mary Johnson"
                  data-testid="referrer-name-input"
                />
              </div>
              
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={referrerForm.email}
                  onChange={(e) => setReferrerForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="mary@example.com"
                  data-testid="referrer-email-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={referrerForm.phone}
                    onChange={(e) => setReferrerForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label>Commission Rate (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={referrerForm.commission_rate}
                    onChange={(e) => setReferrerForm(prev => ({ ...prev, commission_rate: parseFloat(e.target.value) || 10 }))}
                  />
                </div>
              </div>
              
              <div>
                <Label>LinkedIn URL</Label>
                <Input
                  value={referrerForm.linkedin_url}
                  onChange={(e) => setReferrerForm(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  placeholder="https://linkedin.com/in/maryjohnson"
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <textarea
                  value={referrerForm.notes}
                  onChange={(e) => setReferrerForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                  placeholder="Notes about this referrer..."
                />
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReferrerModal(false);
                  setEditingReferrer(null);
                  resetReferrerForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={editingReferrer ? handleUpdateReferrer : handleCreateReferrer}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                data-testid="save-referrer-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                {editingReferrer ? 'Update Referrer' : 'Add Referrer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Calculate Commissions Modal */}
      {showCommissionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Calculate Commissions
                </h3>
                <button
                  onClick={() => setShowCommissionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period Start</Label>
                  <Input
                    type="date"
                    value={commissionForm.period_start}
                    onChange={(e) => setCommissionForm(prev => ({ ...prev, period_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Period End</Label>
                  <Input
                    type="date"
                    value={commissionForm.period_end}
                    onChange={(e) => setCommissionForm(prev => ({ ...prev, period_end: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <Label>Per Employee Rate ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={commissionForm.per_employee_rate}
                  onChange={(e) => setCommissionForm(prev => ({ ...prev, per_employee_rate: parseFloat(e.target.value) || 0 }))}
                  placeholder="25.00"
                />
                <p className="text-xs text-gray-500 mt-1">Monthly subscription cost per employee</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg text-sm">
                <p className="font-medium text-purple-900">How it works:</p>
                <ul className="mt-2 space-y-1 text-purple-700">
                  <li>• Counts current employees for each referred company</li>
                  <li>• Multiplies by per-employee rate</li>
                  <li>• Applies referrer&apos;s commission percentage</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCommissionModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCalculateCommissions}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Calculate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Portal Password Modal */}
      {showPortalPasswordModal && portalPasswordReferrer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Enable Portal Access
                </h3>
                <button
                  onClick={() => setShowPortalPasswordModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Set a password for {portalPasswordReferrer.full_name}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800">
                  <strong>Portal URL:</strong> /referrer-portal
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Once enabled, {portalPasswordReferrer.full_name} can log in to view their commissions and track referred companies.
                </p>
              </div>

              <div>
                <Label className="text-gray-700">Email (Login)</Label>
                <Input
                  value={portalPasswordReferrer.email}
                  disabled
                  className="mt-1.5 bg-gray-50"
                />
              </div>

              <div>
                <Label className="text-gray-700">Password</Label>
                <Input
                  type="password"
                  value={portalPassword}
                  onChange={(e) => setPortalPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-gray-700">Confirm Password</Label>
                <Input
                  type="password"
                  value={portalPasswordConfirm}
                  onChange={(e) => setPortalPasswordConfirm(e.target.value)}
                  placeholder="Confirm password"
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPortalPasswordModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSetPortalPassword}
                disabled={settingPassword || !portalPassword || !portalPasswordConfirm}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
              >
                <Lock className="w-4 h-4" />
                {settingPassword ? 'Enabling...' : 'Enable Portal Access'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
